import React, { useEffect, useState } from 'react';
import {
    Container,
    Box,
    Grid,
    Typography,
    CardContent,
    CardActions,
    Button,
    CircularProgress,
    Paper,
    Divider,
    useTheme,
    useMediaQuery,
    Fade,
    InputAdornment,
    TextField
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import SchoolIcon from '@mui/icons-material/School';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useLanguage } from './LanguageContext';

export default function MyResults() {
    const { t } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [results, setResults] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        document.title = `${t.myResults} | ClassCapture`;
    }, [t]);

    useEffect(() => {
        const fetchResults = async () => {
            try {
                const auth = getAuth();
                const token = await auth.currentUser.getIdToken();
                const url = process.env.REACT_APP_BACKEND_URL + '/quizzes/taken';
                const response = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await response.json();
                setResults(data);
            } catch (err) {
                console.error('Error fetching results', err);
            } finally {
                setLoading(false);
            }
        };
        fetchResults();
    }, []);

    const gradientBackground = {
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
    };

    const filteredResults = results.filter(r =>
        r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <Container sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                gap: 1
            }}>
                <SchoolIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" color="primary" fontWeight={450}>
                    ClassCapture
                </Typography>
                <CircularProgress color="primary" size={24} />
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="lg" sx={{ py: 2 }}>
            {/* Header */}
            <Fade in timeout={800}>
                <Paper
                    elevation={1}
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        ...gradientBackground,
                        color: 'white'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AssessmentIcon sx={{ fontSize: 24, mr: 1, color:"black" }} />
                        <Typography variant="h6" component="h1" fontWeight={450} color={"black"}>
                            {t.myResults}
                        </Typography>
                    </Box>
                    <TextField
                        placeholder={t.search}
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        sx={{
                            width: isMobile ? '100px' : '180px',
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            borderRadius: 1,
                            '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                            '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: 'white' },
                            '& input': { color: 'white', padding: '6px 10px', fontSize: '0.875rem' }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon sx={{ fontSize: 18, color: 'white' }} />
                                </InputAdornment>
                            )
                        }}
                    />
                </Paper>
            </Fade>

            {/* Section Title */}
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', color:"black" }}>
                <Typography variant="subtitle1" component="h2" fontWeight={450}>
                    {t.yourQuizResults}
                </Typography>
                <Divider sx={{ ml: 2, flexGrow: 1 }} />
            </Box>

            {/* Results List */}
            {filteredResults.length > 0 ? (
                <Fade in timeout={1200}>
                    <Grid container spacing={2}>
                        {filteredResults.map(res => (
                            <Grid item xs={12} sm={6} md={4} key={res.quiz_id}>
                                <Paper
                                    elevation={1}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        transition: 'transform 0.15s, box-shadow 0.15s',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 2
                                        }
                                    }}
                                >
                                    <Box sx={{ p: 1, ...gradientBackground, color: 'white', display: 'flex', alignItems: 'center' }}>
                                        <AssessmentIcon sx={{ mr: 1, fontSize: 16, color:"black" }} />
                                        <Typography variant="body2" fontWeight={450} noWrap color="black">
                                            {res.title}
                                        </Typography>
                                    </Box>

                                    <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            mb: 1,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            lineHeight: 1.3
                                        }}>
                                            {res.description || t.noDescriptionGeneric}
                                        </Typography>

                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mt: 2,
                                            p: 1,
                                            bgcolor: theme.palette.grey[50],
                                            borderRadius: 1,
                                            border: `1px solid ${theme.palette.grey[200]}`
                                        }}>
                                            <Typography variant="body2" fontWeight={450} color="primary">
                                                {t.scoreLabel} {res.quizScore}/{res.numQuestions}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    ml: 'auto',
                                                    bgcolor: theme.palette.primary.main,
                                                    color: 'white',
                                                    px: 0.75,
                                                    py: 0.25,
                                                    borderRadius: 0.5
                                                }}
                                            >
                                                {Math.round((res.quizScore / res.numQuestions) * 100)}%
                                            </Typography>
                                        </Box>
                                    </CardContent>

                                    <Divider />

                                    <CardActions sx={{ p: 0 }}>
                                        <Button
                                            fullWidth
                                            onClick={() => navigate(`/result/${res.quiz_id}`)}
                                            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                                            sx={{
                                                p: 0.75,
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                justifyContent: 'space-between',
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {t.viewDetails}
                                        </Button>
                                    </CardActions>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Fade>
            ) : (
                <Fade in timeout={1200}>
                    <Paper
                        elevation={1}
                        sx={{
                            p: 2,
                            textAlign: 'center',
                            borderRadius: 1,
                            backgroundColor: 'background.paper'
                        }}
                    >
                        {searchTerm ? (
                            <>
                                <InfoOutlinedIcon sx={{ fontSize: 24, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="body2" gutterBottom>
                                    {t.noMatchingResults}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t.noMatchingResultsDesc}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <AssessmentIcon sx={{ fontSize: 24, color: 'text.secondary', mb: 1 }} />
                                <Typography variant="body2" gutterBottom>
                                    {t.noQuizResultsAvailable}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t.noQuizResultsAvailableDesc}
                                </Typography>
                            </>
                        )}
                    </Paper>
                </Fade>
            )}
        </Container>
    );
}
